import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Temporadas.css';
import { borrarLicencia, getLicencias, guardarLicencia } from '../../services/licenciasService';
import { actualizarJugadorReal } from '../../services/jugadoresService';
import { useAuth } from '../../context/AuthContext';
import ModalCorreoPrueba from './ModalCorreoPrueba';
import ModalHistorialEnvios from './ModalHistorialEnvios';
import { accionesDeFila, filaDeServidor, hayCambios, resumenFaltas } from './licenciasBorrador';

// Correo y licencia (nº y caducidad) de cada jugador con club en la temporada.
// El correo recibe los avisos automáticos; la caducidad de la licencia avisa de
// que toca renovar el reconocimiento médico. Cada fila se guarda por separado.
export default function LicenciasTab({ temporada, estado }) {
  const [filas, setFilas] = useState([]);
  const [originales, setOriginales] = useState({}); // { [jugadorId]: { email, numero, fecha } }
  const [borradores, setBorradores] = useState({});
  const [feedback, setFeedback] = useState({}); // { [jugadorId]: { tipo, msg } }
  const [guardando, setGuardando] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);

  const { usuario } = useAuth();
  // El correo de prueba (manda un correo real) y el histórico de envíos (datos personales): solo ADMIN, no el comité.
  const esAdmin = usuario?.rol === 'ADMIN';
  const [correoPrueba, setCorreoPrueba] = useState(null); // fila a la que se va a mandar el correo
  const [historial, setHistorial] = useState(null); // fila cuyo histórico de envíos se está viendo

  const soloLectura = estado === 'FINALIZADA';

  const cargar = useCallback(() => {
    setCargando(true);
    return getLicencias(temporada)
      .then((datos) => {
        setFilas(datos);
        const base = Object.fromEntries(datos.map((f) => [f.jugador_id, filaDeServidor(f)]));
        setOriginales(base);
        setBorradores(base);
        setFeedback({});
        setError(null);
      })
      .catch((err) => setError(err.message || 'No se pudieron cargar las licencias.'))
      .finally(() => setCargando(false));
  }, [temporada]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiar = (id, campo, valor) => setBorradores((prev) => ({ ...prev, [id]: { ...prev[id], [campo]: valor } }));
  const poner = (id, tipo, msg) => setFeedback((prev) => ({ ...prev, [id]: { tipo, msg } }));

  const guardarFila = async (fila) => {
    const id = fila.jugador_id;
    const acciones = accionesDeFila(originales[id], borradores[id]);
    if (acciones.error) { poner(id, 'error', acciones.error); return; }

    setGuardando(id);
    poner(id, 'ok', '');
    try {
      let emailGuardado = originales[id].email;
      if (acciones.email !== undefined) {
        await actualizarJugadorReal(id, { email: acciones.email });
        emailGuardado = acciones.email ?? '';
      }
      if (acciones.licencia === 'guardar') {
        await guardarLicencia(temporada, id, { numeroLicencia: borradores[id].numero.trim(), fechaExpiracion: borradores[id].fecha });
      } else if (acciones.licencia === 'borrar') {
        await borrarLicencia(temporada, id);
      }
      const nuevo = { ...borradores[id], email: emailGuardado, numero: borradores[id].numero.trim() };
      setOriginales((prev) => ({ ...prev, [id]: nuevo }));
      setBorradores((prev) => ({ ...prev, [id]: nuevo }));
      setFilas((prev) => prev.map((f) => (f.jugador_id === id
        ? { ...f, email: emailGuardado || null, numero_licencia: nuevo.numero || null, fecha_expiracion: nuevo.numero ? nuevo.fecha : null }
        : f)));
      poner(id, 'ok', 'Guardado.');
    } catch (err) {
      poner(id, 'error', err.message || 'No se pudo guardar.');
    } finally {
      setGuardando(null);
    }
  };

  const correoEnviado = (fila, email) => {
    poner(fila.jugador_id, 'ok', `Correo de prueba enviado a ${email}.`);
    setCorreoPrueba(null);
  };

  const visibles = useMemo(() => {
    const t = buscar.trim().toLowerCase();
    return t ? filas.filter((f) => `${f.nombre} ${f.alias ?? ''} ${f.club}`.toLowerCase().includes(t)) : filas;
  }, [filas, buscar]);
  const faltas = useMemo(() => resumenFaltas(filas), [filas]);

  if (cargando) return <p className="empty-msg">Cargando licencias…</p>;
  if (error) return <p className="error-msg" role="alert">{error}</p>;

  return (
    <div className="licencias-tab">
      <p className="licencias-ayuda">
        El <strong>correo</strong> recibe los avisos automáticos (aplazamientos, anulaciones, plazos) y la{' '}
        <strong>caducidad</strong> avisa de que toca renovar el reconocimiento médico. Solo aparecen jugadores con club en
        esta temporada; asígnalos antes en «Por club».
      </p>

      <div className="licencias-barra">
        <input
          type="search" aria-label="Buscar jugador o club" placeholder="Buscar jugador o club…"
          value={buscar} onChange={(e) => setBuscar(e.target.value)}
        />
        <span className="licencias-faltas">{faltas.sinCorreo} sin correo · {faltas.sinLicencia} sin licencia (de {filas.length})</span>
      </div>

      {soloLectura && <p className="empty-msg">Temporada finalizada: solo lectura.</p>}

      {visibles.length === 0 ? (
        <p className="empty-msg">No hay jugadores con club en esta temporada.</p>
      ) : (
        <table className="data-table licencias-tabla">
          <thead>
            <tr><th>Club</th><th>Jugador</th><th>Correo</th><th>Nº licencia</th><th>Caduca</th><th /></tr>
          </thead>
          <tbody>
            {visibles.map((f) => {
              const id = f.jugador_id;
              const b = borradores[id] ?? filaDeServidor(f);
              const sucia = hayCambios(originales[id] ?? b, b);
              const fb = feedback[id];
              return (
                <tr key={id}>
                  <td data-label="Club">{f.club}</td>
                  <td data-label="Jugador">{f.alias ? `${f.nombre} (${f.alias})` : f.nombre}</td>
                  <td data-label="Correo">
                    <input type="email" aria-label={`Correo de ${f.nombre}`} value={b.email} disabled={soloLectura}
                      onChange={(e) => cambiar(id, 'email', e.target.value)} placeholder="sin correo" />
                  </td>
                  <td data-label="Nº licencia">
                    <input aria-label={`Número de licencia de ${f.nombre}`} value={b.numero} disabled={soloLectura}
                      onChange={(e) => cambiar(id, 'numero', e.target.value)} />
                  </td>
                  <td data-label="Caduca">
                    <input type="date" aria-label={`Caducidad de la licencia de ${f.nombre}`} value={b.fecha} disabled={soloLectura}
                      onChange={(e) => cambiar(id, 'fecha', e.target.value)} />
                  </td>
                  <td data-label="">
                    <button type="button" className="btn btn-primary btn-sm" aria-label={`Guardar ${f.nombre}`}
                      disabled={soloLectura || !sucia || guardando === id} onClick={() => guardarFila(f)}>
                      {guardando === id ? 'Guardando…' : 'Guardar'}
                    </button>
                    {esAdmin && (
                      <button type="button" className="btn btn-secondary btn-sm" aria-label={`Ver histórico de envíos de ${f.nombre}`}
                        onClick={() => setHistorial(f)}>
                        Histórico
                      </button>
                    )}
                    {esAdmin && (
                      <button type="button" className="btn btn-secondary btn-sm" aria-label={`Enviar correo de prueba a ${f.nombre}`}
                        disabled={!originales[id]?.email.trim() || b.email.trim() !== originales[id].email.trim()}
                        title={!originales[id]?.email.trim() ? 'Este jugador no tiene correo guardado' : sucia && b.email.trim() !== originales[id].email.trim() ? 'Guarda primero el cambio de correo' : 'Enviar un correo de prueba'}
                        onClick={() => setCorreoPrueba(f)}>
                        Probar correo
                      </button>
                    )}
                    {fb?.msg && <span className={fb.tipo === 'error' ? 'msg-error licencias-fb' : 'licencias-fb licencias-fb-ok'} role={fb.tipo === 'error' ? 'alert' : 'status'}>{fb.msg}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {historial && <ModalHistorialEnvios jugador={historial} onCerrar={() => setHistorial(null)} />}

      {correoPrueba && (
        <ModalCorreoPrueba
          jugador={correoPrueba} email={originales[correoPrueba.jugador_id].email.trim()}
          onEnviado={(email) => correoEnviado(correoPrueba, email)} onCerrar={() => setCorreoPrueba(null)}
        />
      )}
    </div>
  );
}
